import { Box, Stack, Typography } from '@mui/material';
import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import datlabLogo from '../assets/datlab_studio_logo.png';
import { checkTauriPresentSync } from '../services/backend/isDesktop.js';
import HandleTop from './ui/HandleTop';
import WindowBackground from './ui/WindowBackground';

const About = ({ onClose }) => {
  const [versionInfo, setVersionInfo] = useState({
    version: '0.1.0',
    releaseDate: '29-08-2025',
    commit: 'unknown',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try to fetch version.json from public folder first
    // Fallback to backend API endpoint if static file is not available
    const fetchVersion = async () => {
      // Build metadata (release date, commit) comes from version.json; in the
      // desktop app the authoritative version is the one the Tauri shell was
      // built with — the same one the updater compares against the manifest.
      let appVersion = null;
      if (checkTauriPresentSync()) {
        try {
          const { getVersion } = await import('@tauri-apps/api/app');
          appVersion = await getVersion();
        } catch (error) {
          console.warn('Could not read app version from Tauri:', error);
        }
      }
      const withAppVersion = (data) => ({
        version: appVersion || data.version || '0.1.0',
        releaseDate:
          data.releaseDate ||
          new Date()
            .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
            .replace(/\//g, '-'),
        commit: data.commit || 'unknown',
      });

      try {
        // Try static file first
        const res = await fetch('/version.json');
        if (res.ok) {
          setVersionInfo(withAppVersion(await res.json()));
          return;
        }
      } catch (error) {
        console.warn('Could not load version.json from static file:', error);
      }

      // Fallback to backend API
      try {
        const res = await fetch('/api/version');
        if (res.ok) {
          setVersionInfo(withAppVersion(await res.json()));
          return;
        }
      } catch (error) {
        console.warn('Could not load version from API:', error);
      }

      // Keep default values (but the real app version) if both fail
      if (appVersion) setVersionInfo((prev) => ({ ...prev, version: appVersion }));
    };

    fetchVersion().finally(() => {
      setLoading(false);
    });
  }, []);

  return (
    <Box
      sx={{
        height: '560px',
        width: '920px',
        display: 'flex',
        flexDirection: 'column',
        gap: '1px',
        boxShadow:
          '0 33px 9px 0 rgba(67, 79, 86, 0.01), 0 21px 8px 0 rgba(67, 79, 86, 0.07), 0 12px 7px 0 rgba(67, 79, 86, 0.25), 0 5px 5px 0 rgba(67, 79, 86, 0.43), 0 1px 3px 0 rgba(67, 79, 86, 0.49)',
        background: 'transparent',
      }}
    >
      <HandleTop height="34px" onIconClick={onClose} iconAriaLabel="Close About">
        <X />
      </HandleTop>
      <WindowBackground radius="16px">
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            padding: '32px',
            gap: '20px',
          }}
        >
          <Box
            component="img"
            src={datlabLogo}
            alt="DatLab Studio Logo"
            sx={{ userSelect: 'none', marginBottom: '32px' }}
          />

          <Stack spacing={1} sx={{ width: '100%', maxWidth: '640px' }}>
            <Typography variant="body1">Version: {versionInfo.version}</Typography>
            <Typography variant="body1">Release date: {versionInfo.releaseDate}</Typography>
            <Typography variant="body1">Commit: {versionInfo.commit}</Typography>
            {/* Company links hidden temporarily */}
            {/* <Typography variant="body1">Company: <a href="https://www.oroboros.at" target="_blank" rel="noopener noreferrer">Oroboros Instruments</a></Typography>
            <Typography variant="body1">Contact: <a href="mailto:support@oroboros.at" target="_blank" rel="noopener noreferrer">support@oroboros.at</a></Typography>
            <Typography variant="body1">Website: <a href="https://www.oroboros.at/datlab-studio" target="_blank" rel="noopener noreferrer">https://www.oroboros.at/datlab-studio</a></Typography> */}
          </Stack>
        </Box>
      </WindowBackground>
    </Box>
  );
};

export default About;
